import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Badge as BadgeIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import { useAppSelector } from '../../redux/hooks';
import { useGetJobDescriptionByUserIdQuery } from '../../redux/jobDescriptionApi';

const MyJobDescription: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAppSelector(state => state.auth.user);
  
  const { data: jobDescription, isLoading, isError, error } = useGetJobDescriptionByUserIdQuery(
    { userId: currentUser?.id || '' },
    { skip: !currentUser?.id }
  );

  if (isLoading) {
    return (
      <PageWithHeader>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </PageWithHeader>
    );
  }

  if (isError || !jobDescription) {
    return (
      <PageWithHeader>
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={() => navigate('/reviews/bsc')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">My Job Description</Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            No job description has been assigned to you yet. You can create your own job description.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => navigate('/reviews/job-descriptions/my/edit')}
            >
              Create Job Description
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/reviews/bsc')}
            >
              Back to BSC
            </Button>
          </Box>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/reviews/bsc')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">{jobDescription.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {jobDescription.department} • Grade: {jobDescription.grade || 'N/A'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate('/reviews/job-descriptions/my/edit')}
          >
            Edit
          </Button>
          <Chip 
            label={jobDescription.isActive ? 'Active' : 'Inactive'} 
            color={jobDescription.isActive ? 'success' : 'default'}
          />
        </Box>

        <Grid container spacing={3}>
          {/* Job Purpose */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Job Purpose
              </Typography>
              <Typography variant="body1">
                {jobDescription.jobPurpose || 'No job purpose specified.'}
              </Typography>
            </Paper>
          </Grid>

          {/* Accountability Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary">
                Accountability
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><BadgeIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Reports To" 
                    secondary={jobDescription.reportsToUserId ? 'Assigned Supervisor' : 'Not specified'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WorkIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Direct Reports" 
                    secondary={jobDescription.directReportsCount || 0}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WorkIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Indirect Reports" 
                    secondary={jobDescription.indirectReportsCount || 0}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* Qualifications */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom color="primary">
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Minimum Qualifications
              </Typography>
              {jobDescription.minimumQualifications && jobDescription.minimumQualifications.length > 0 ? (
                <List dense>
                  {jobDescription.minimumQualifications.map((qual, idx) => (
                    <ListItem key={idx}>
                      <ListItemText 
                        primary={qual.name}
                        secondary={`${qual.level || ''} • ${qual.field || ''} ${qual.isRequired ? '(Required)' : '(Preferred)'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">No qualifications specified.</Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Minimum Experience
              </Typography>
              <Typography variant="body2">
                {jobDescription.minimumExperienceYears 
                  ? `${jobDescription.minimumExperienceYears} years` 
                  : 'Not specified'}
              </Typography>
            </Paper>
          </Grid>

          {/* Key Result Areas from BSC Perspectives */}
          {jobDescription.bscPerspectives && jobDescription.bscPerspectives.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Key Result Areas (KRAs) by BSC Perspective
                </Typography>
                <Grid container spacing={2}>
                  {jobDescription.bscPerspectives.map((perspective, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                            {perspective.name}
                          </Typography>
                          {perspective.kras.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {perspective.kras.map((kra, kraIdx) => (
                                <Chip
                                  key={kraIdx}
                                  label={kra}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                              No KRAs defined
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Authority Limits */}
          {jobDescription.limitsToAuthority && jobDescription.limitsToAuthority.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Limits to Authority
                </Typography>
                <Grid container spacing={2}>
                  {jobDescription.limitsToAuthority.map((limit, idx) => (
                    <Grid item xs={12} md={6} key={idx}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Chip 
                            label={limit.category} 
                            size="small" 
                            color="primary" 
                            sx={{ mb: 1 }} 
                          />
                          <Typography variant="body1" fontWeight="medium" gutterBottom>
                            {limit.description}
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {/* Category-specific limit display */}
                            {(limit.category === 'Financial' || limit.category === 'Procurement') && limit.monetaryLimit && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Limit:</strong> {limit.currency || 'USD'} {limit.monetaryLimit.toLocaleString()}
                              </Typography>
                            )}
                            {limit.category === 'HR/Personnel' && (
                              <>
                                {limit.headcountLimit && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Headcount:</strong> Up to {limit.headcountLimit} positions
                                  </Typography>
                                )}
                                {limit.salaryBandLimit && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Salary Band:</strong> {limit.salaryBandLimit}
                                  </Typography>
                                )}
                              </>
                            )}
                            {limit.category === 'Operational' && limit.unitLimit && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Limit:</strong> {limit.unitLimit} {limit.unitType || 'units'}
                              </Typography>
                            )}
                            {limit.category === 'Legal/Contracts' && (
                              <>
                                {limit.contractDurationLimit && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Duration:</strong> {limit.contractDurationLimit}
                                  </Typography>
                                )}
                                {limit.contractValueLimit && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Value:</strong> ${limit.contractValueLimit.toLocaleString()}
                                  </Typography>
                                )}
                              </>
                            )}
                            {limit.category === 'IT/Technology' && (
                              <>
                                {limit.accessLevel && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Access:</strong> {limit.accessLevel}
                                  </Typography>
                                )}
                                {limit.systemScope && (
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Scope:</strong> {limit.systemScope}
                                  </Typography>
                                )}
                              </>
                            )}
                            {limit.approvalRequired && (
                              <Typography variant="body2" color="warning.main">
                                <strong>Approval beyond limit:</strong> {limit.approvalRequired}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* Professional Registrations */}
          {jobDescription.professionalRegistrations && jobDescription.professionalRegistrations.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Professional Registrations
                </Typography>
                <List dense>
                  {jobDescription.professionalRegistrations.map((reg, idx) => (
                    <ListItem key={idx}>
                      <ListItemText 
                        primary={reg.body}
                        secondary={`${reg.membershipType || ''} ${reg.isRequired ? '(Required)' : '(Preferred)'}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Other Requirements */}
          {jobDescription.otherRequirements && jobDescription.otherRequirements.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Other Requirements
                </Typography>
                <List dense>
                  {jobDescription.otherRequirements.map((req, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon><InfoIcon /></ListItemIcon>
                      <ListItemText primary={req} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    </PageWithHeader>
  );
};

export default MyJobDescription;
